import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { useParams } from "react-router-dom";
import { API_URL, FRONTEND_URL, WS_URL } from "../config";
import { backendErrorMessage, logWebSocketError, readJsonResponse } from "../apiDiagnostics";
import PageShell from "../components/PageShell";

function TeacherView() {
  const { id, secret } = useParams();

  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("Verbinde...");
  const [deleted, setDeleted] = useState(false);
  const [printMode, setPrintMode] = useState("list");

  async function loadTeacherView() {
    const url = `${API_URL}/api/teacher/${id}/${secret}`;
    try {
      const response = await fetch(url);
      const data = await readJsonResponse(response, "Lehreransicht laden", url);

      if (response.ok) {
        setList(data);
        setError("");
      } else {
        setError(data.error || "Lehreransicht nicht verfügbar.");
      }
    } catch (err) {
      setError(backendErrorMessage("Lehreransicht laden", url, err));
    }
  }

  useEffect(() => {
    loadTeacherView();

    let ws;
    let reconnectTimer;

    function connectWebSocket() {
      const wsUrl = `${WS_URL}?listId=${id}`;
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnectionStatus("Live verbunden");
      };

      ws.onmessage = event => {
        const data = JSON.parse(event.data);

        if (data.type === "LIST_UPDATED") {
          setList(data.list);
        }
      };

      ws.onerror = event => {
        logWebSocketError(wsUrl, event);
        setConnectionStatus("Live-Verbindung gestört");
      };

      ws.onclose = () => {
        setConnectionStatus("Verbindung wird neu aufgebaut...");

        reconnectTimer = setTimeout(() => {
          connectWebSocket();
        }, 3000);
      };
    }

    connectWebSocket();

    return () => {
      clearTimeout(reconnectTimer);

      if (ws) {
        ws.close();
      }
    };
  }, [id, secret]);

  useEffect(() => {
    const resetPrintMode = () => setPrintMode("list");
    window.addEventListener("afterprint", resetPrintMode);
    return () => window.removeEventListener("afterprint", resetPrintMode);
  }, []);

  async function closeList() {
    const url = `${API_URL}/api/teacher/${id}/${secret}/close`;
    try {
      const response = await fetch(url, {
        method: "POST"
      });

      const data = await readJsonResponse(response, "Liste schliessen", url);

      if (response.ok) {
        setList(data);
      } else {
        setError(data.error || "Liste konnte nicht geschlossen werden.");
      }
    } catch (err) {
      setError(backendErrorMessage("Liste schliessen", url, err));
    }
  }

  async function reopenList() {
    const url = `${API_URL}/api/teacher/${id}/${secret}/reopen`;
    try {
      const response = await fetch(url, {
        method: "POST"
      });

      const data = await readJsonResponse(response, "Liste wieder öffnen", url);

      if (response.ok) {
        setList(data);
      } else {
        setError(data.error || "Liste konnte nicht wieder geöffnet werden.");
      }
    } catch (err) {
      setError(backendErrorMessage("Liste wieder öffnen", url, err));
    }
  }

  async function deleteList() {
    const confirmed = window.confirm(
      "Diese Liste wirklich endgültig löschen? Die Einträge sind danach nicht mehr erreichbar."
    );

    if (!confirmed) {
      return;
    }

    setError("");

    const url = `${API_URL}/api/teacher/${id}/${secret}/delete`;
    try {
      const response = await fetch(url, {
        method: "POST"
      });

      if (response.ok) {
        setDeleted(true);
        setList(null);
        return;
      }

      let message = "Liste konnte nicht gelöscht werden.";

      try {
        const data = await readJsonResponse(response, "Liste löschen", url);
        message = data.error || message;
      } catch (jsonError) {
        message = `Liste konnte nicht gelöscht werden. Serverantwort: ${response.status}`;
      }

      setError(message);
    } catch (err) {
      console.error("[Backend] Liste löschen fehlgeschlagen, Formular-Fallback wird versucht", {
        url,
        message: err?.message,
        error: err
      });
      submitDeleteFormFallback();
    }
  }

  function submitDeleteFormFallback() {
    const iframeName = "delete-list-fallback-frame";
    const existingFrame = document.querySelector(`iframe[name="${iframeName}"]`);

    if (existingFrame) {
      existingFrame.remove();
    }

    const iframe = document.createElement("iframe");
    iframe.name = iframeName;
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const form = document.createElement("form");
    form.action = `${API_URL}/api/teacher/${id}/${secret}/delete`;
    form.method = "POST";
    form.target = iframeName;
    form.style.display = "none";
    document.body.appendChild(form);
    form.submit();
    form.remove();

    setDeleted(true);
    setList(null);
  }

  function printPdf() {
    setPrintMode("list");
    window.print();
  }

  function printFlyer() {
    setPrintMode("flyer");
    window.setTimeout(() => window.print(), 0);
  }

  async function saveSettings(nextSettings) {
    const url = `${API_URL}/api/teacher/${id}/${secret}/settings`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          showNames: nextSettings.showNames,
          allowParticipantItems: nextSettings.allowParticipantItems
        })
      });
      const data = await readJsonResponse(response, "Einstellungen speichern", url);
      if (response.ok) setList(data);
      else setError(data.error || "Einstellungen konnten nicht gespeichert werden.");
    } catch (err) {
      setError(backendErrorMessage("Einstellungen speichern", url, err));
    }
  }

  async function updateItemCount(itemId, maxCount) {
    const url = `${API_URL}/api/teacher/${id}/${secret}/items/${itemId}`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ maxCount })
      });
      const data = await readJsonResponse(response, "Anzahl speichern", url);
      if (response.ok) setList(data);
      else setError(data.error || "Anzahl konnte nicht gespeichert werden.");
    } catch (err) {
      setError(backendErrorMessage("Anzahl speichern", url, err));
    }
  }

  if (deleted) {
    return (
      <PageShell maxWidth="max-w-4xl">
        <div className="border border-[#dce8c8] bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold tracking-tight">
            Liste gelöscht
          </h1>

          <p className="mt-4 text-base leading-7 text-neutral-600">
            Diese Mitbringliste wurde endgültig entfernt und ist nicht mehr erreichbar.
          </p>
        </div>
      </PageShell>
    );
  }

  if (!list) {
    return (
      <PageShell maxWidth="max-w-4xl">
        <div className="border border-[#dce8c8] bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold tracking-tight">
            Lehreransicht wird geladen...
          </h1>

          {error && (
            <p className="mt-4 text-sm font-medium text-red-600">
              {error}
            </p>
          )}
        </div>
      </PageShell>
    );
  }

  const isOrdnung = list.listType === "ordnung";
  const studentUrl = `${FRONTEND_URL}/liste/${id}`;

  return (
    <PageShell maxWidth="max-w-4xl">
        <section className="mb-10 border-b border-[#dce8c8] pb-8 print:hidden">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="mb-3 text-xs font-medium uppercase tracking-[0.22em] text-neutral-500">
                Lehreransicht
              </p>

              <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">
                {list.title}
              </h1>

              <p className="mt-4 max-w-2xl text-base leading-7 text-neutral-600">
                Diese Ansicht aktualisiert sich automatisch, sobald sich Schüler eintragen.
              </p>

              <p className="mt-3 text-sm text-neutral-500">
                Status: {connectionStatus}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {!list.closed ? (
                <button
                  className="border border-[#172017] bg-[#172017] px-6 py-3 text-sm font-medium text-white shadow-sm hover:bg-[#263826]"
                  onClick={closeList}
                >
                  Liste schließen
                </button>
              ) : (
                <>
                  <button
                    className="border border-[#172017] bg-[#172017] px-6 py-3 text-sm font-medium text-white shadow-sm hover:bg-[#263826]"
                    onClick={reopenList}
                  >
                    Liste wieder öffnen
                  </button>

                  <button
                    className="border border-[#172017] bg-white px-6 py-3 text-sm font-medium text-[#172017] shadow-sm hover:bg-[#f1f5e8]"
                    onClick={printPdf}
                  >
                    Als PDF speichern
                  </button>
                </>
              )}

              <button
                className="border border-[#172017] bg-white px-6 py-3 text-sm font-medium text-[#172017] shadow-sm hover:bg-[#f1f5e8]"
                onClick={printFlyer}
              >
                Flyer drucken
              </button>

              <button
                className="border border-red-300 bg-red-50 px-6 py-3 text-sm font-medium text-red-700 hover:bg-red-100"
                onClick={deleteList}
              >
                Liste löschen
              </button>
            </div>
          </div>
        </section>

        {error && (
          <div className="mb-6 border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 print:hidden">
            {error}
          </div>
        )}

        <section className="mb-8 border border-[#dce8c8] bg-white p-6 shadow-sm print:hidden">
          <h2 className="text-xl font-semibold tracking-tight">Zusatzfunktionen</h2>

          <label className="mt-4 flex items-start gap-3 border border-[#dce8c8] bg-[#f8faf2] p-4">
            <input
              className="mt-1 rounded border-[#c9d9b4] text-[#39501f] focus:ring-[#6d8f36]"
              type="checkbox"
              checked={list.showNames}
              onChange={e => saveSettings({
                showNames: e.target.checked,
                allowParticipantItems: list.allowParticipantItems
              })}
            />
            <span>
              <span className="block text-sm font-semibold text-neutral-800">Namen der Teilnehmer anzeigen</span>
              <span className="mt-1 block text-sm text-neutral-500">Wenn ausgeschaltet, sehen Teilnehmer nur belegte Mengen ohne fremde Namen.</span>
            </span>
          </label>

          <label className="mt-3 flex items-start gap-3 border border-[#dce8c8] bg-[#f8faf2] p-4">
            <input
              className="mt-1 rounded border-[#c9d9b4] text-[#39501f] focus:ring-[#6d8f36]"
              type="checkbox"
              checked={list.allowParticipantItems}
              onChange={e => saveSettings({
                showNames: list.showNames,
                allowParticipantItems: e.target.checked
              })}
            />
            <span>
              <span className="block text-sm font-semibold text-neutral-800">Teilnehmer dürfen Einträge ergänzen</span>
              <span className="mt-1 block text-sm text-neutral-500">Wenn aktiviert, erscheint in der Teilnehmeransicht ein Formular für neue Einträge.</span>
            </span>
          </label>
        </section>

        <section className={`mb-8 border border-[#dce8c8] bg-white p-6 shadow-sm print:border-0 print:shadow-none print:p-0 ${printMode === "flyer" ? "print:hidden" : ""}`}>
          <h1 className="text-3xl font-semibold tracking-tight">
            {list.title}
          </h1>

          <p className="mt-2 text-sm text-neutral-500">
            Status: {list.closed ? "Geschlossen" : "Offen"}
          </p>

          <p className="mt-2 text-sm font-medium text-[#39501f]">
            {isOrdnung
              ? (list.singleChoice
                ? "Modus: Jeder Name kann nur einen Eintrag belegen."
                : "Modus: Schüler können mehrere Einträge belegen, aber jeden Eintrag nur einmal.")
              : "Modus: Mitbringliste mit Mengenangabe."}
          </p>
        </section>

        <section className={`space-y-4 ${printMode === "flyer" ? "print:hidden" : ""}`}>
          {list.items.map(item => {
            const usedAmount = item.assignees.reduce(
              (sum, person) => sum + Number(person.quantity || 1),
              0
            );

            const full = usedAmount >= item.max_count;

            return (
              <article
                className="border border-[#dce8c8] bg-white p-5 shadow-sm print:break-inside-avoid print:shadow-none"
                key={item.id}
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold tracking-tight">
                      {item.name}
                    </h2>

                    <p className="mt-1 text-sm text-neutral-500">
                      {usedAmount} von {item.max_count} vergeben
                    </p>

                    <label className="mt-3 block text-sm font-medium text-neutral-700 print:hidden">
                      Anzahl
                      <input
                        className="mt-1 w-28 border-[#c9d9b4] px-3 py-2 text-sm focus:border-[#6d8f36] focus:ring-[#6d8f36]"
                        type="number"
                        min={Math.max(1, usedAmount)}
                        value={item.max_count}
                        onChange={e => updateItemCount(item.id, e.target.value)}
                      />
                    </label>
                  </div>

                  <span
                    className={
                      full
                        ? "w-fit border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700"
                        : "w-fit border border-[#dce8c8] bg-[#eef5df] px-3 py-1 text-xs font-semibold text-[#39501f]"
                    }
                  >
                    {full ? "Voll" : "Offen"}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {item.assignees.length === 0 ? (
                    <p className="text-sm text-neutral-500">
                      Noch niemand eingetragen.
                    </p>
                  ) : (
                    item.assignees.map(person => (
                      <span
                        className="border border-[#dce8c8] bg-[#f8faf2] px-3 py-2 text-sm font-medium text-neutral-700"
                        key={person.id}
                      >
                        {person.name}{!isOrdnung && <> · {person.quantity}x</>}
                      </span>
                    ))
                  )}
                </div>
              </article>
            );
          })}
        </section>

        {printMode === "flyer" && (
          <section className="hidden print:block">
            <div className="mx-auto flex min-h-[250mm] max-w-[190mm] flex-col items-center justify-center text-center">
              <h1 className="text-5xl font-semibold tracking-tight">{list.title}</h1>
              <p className="mt-8 text-2xl text-neutral-700">Bitte scannen und direkt eintragen.</p>
              <div className="mt-10 rounded-2xl bg-white p-6">
                <QRCode value={studentUrl} size={320} />
              </div>
              <p className="mt-8 break-all text-lg text-neutral-600">{studentUrl}</p>
            </div>
          </section>
        )}
    </PageShell>
  );
}

export default TeacherView;
